/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * AcceptInvitePage Component
 * ============================================================================
 *
 * Page for accepting organization invitations by setting a new password.
 * Users arrive here from an email invitation link with their email and
 * temporary password as query parameters.
 *
 * ============================================================================
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserPlus, AlertCircle, Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { AuthLayout } from '@/components/auth/AuthLayout';
import { Button } from '@/components/ui/Button';
import { FormInput } from '@/components/ui/FormInput';
import { PasswordStrengthIndicator } from '@/components/ui/PasswordStrengthIndicator';
import { useAcceptInvite } from '@/hooks/api/useAuth';
import { useDeviceFingerprint, getDeviceFingerprintSync } from '@/hooks/useDeviceFingerprint';
import { getErrorMessage, isApiError } from '@/services/api/errors';

export function AcceptInvitePage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fingerprint } = useDeviceFingerprint();
  const acceptInviteMutation = useAcceptInvite();

  // Get email and temp password from URL
  const email = searchParams.get('email') || '';
  const tempPassword = searchParams.get('temp') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validate URL parameters
  useEffect(() => {
    if (!email || !tempPassword) {
      setError(t('auth.acceptInvite.invalidLink', 'Invalid invitation link. Please check your email for the correct link.'));
    }
  }, [email, tempPassword, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!newPassword) {
      setError(t('auth.acceptInvite.passwordRequired', 'Please enter a new password'));
      return;
    }

    if (newPassword.length < 8) {
      setError(t('auth.acceptInvite.passwordMinLength', 'Password must be at least 8 characters'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('auth.acceptInvite.passwordMismatch', 'Passwords do not match'));
      return;
    }

    // Get device fingerprint
    const deviceFp = fingerprint ?? getDeviceFingerprintSync();

    acceptInviteMutation.mutate(
      {
        email,
        tempPassword,
        newPassword,
        consentAccepted,
        deviceFingerprint: deviceFp.hash,
        browser: deviceFp.info.browser,
        browserVersion: deviceFp.info.browserVersion,
        os: deviceFp.info.os,
        osVersion: deviceFp.info.osVersion,
      },
      {
        onError: (err) => {
          if (isApiError(err)) {
            setError(err.message || getErrorMessage(err.code));
          } else {
            setError(t('auth.acceptInvite.error', 'Failed to accept invitation. Please try again.'));
          }
        },
      }
    );
  };

  // Show error if invalid link
  if (!email || !tempPassword) {
    return (
      <AuthLayout
        title={t('auth.acceptInvite.title', 'Accept Invitation')}
        subtitle={t('auth.acceptInvite.subtitle', 'Set your password to join the organization')}
        icon={<UserPlus size={28} />}
      >
        <div className="accept-invite-error">
          <AlertCircle size={48} className="error-icon" />
          <p className="error-text">
            {t('auth.acceptInvite.invalidLink', 'Invalid invitation link. Please check your email for the correct link.')}
          </p>
          <Button
            variant="secondary"
            onClick={() => navigate('/auth/login')}
          >
            {t('auth.acceptInvite.goToLogin', 'Go to Login')}
          </Button>
        </div>

        <style>{`
          .accept-invite-error {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
            padding: 32px;
            text-align: center;
          }

          .error-icon {
            color: var(--color-error);
          }

          .error-text {
            color: var(--color-text-muted);
            font-size: var(--text-base);
            line-height: 1.6;
          }

          @media (max-width: 640px) {
            .accept-invite-error {
              padding: 1rem;
            }
          }

          @media (prefers-reduced-motion: reduce) {
            .accept-invite-error-msg { animation: none !important; }
          }
        `}</style>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.acceptInvite.title', 'Accept Invitation')}
      subtitle={t('auth.acceptInvite.subtitle', 'Set your password to join the organization')}
      icon={<UserPlus size={28} />}
    >
      <form onSubmit={handleSubmit} className="accept-invite-form">
        {/* Error message */}
        {error && (
          <div className="accept-invite-error-msg">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Success hint */}
        <div className="accept-invite-info">
          <CheckCircle size={16} />
          <span>{t('auth.acceptInvite.welcomeText', 'Welcome! Please set a secure password for your account.')}</span>
        </div>

        {/* Email (read-only) */}
        <FormInput
          label={t('auth.acceptInvite.email', 'Email')}
          type="email"
          value={email}
          disabled
          leftIcon={<Lock size={18} />}
        />

        {/* New Password */}
        <div className="password-field">
          <FormInput
            label={t('auth.acceptInvite.newPassword', 'New Password')}
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder={t('auth.acceptInvite.newPasswordPlaceholder', 'Enter your new password')}
            required
            autoFocus
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowNewPassword(!showNewPassword)}
            tabIndex={-1}
          >
            {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        {/* Password strength indicator */}
        {newPassword && <PasswordStrengthIndicator password={newPassword} />}

        {/* Confirm Password */}
        <div className="password-field">
          <FormInput
            label={t('auth.acceptInvite.confirmPassword', 'Confirm Password')}
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t('auth.acceptInvite.confirmPasswordPlaceholder', 'Confirm your new password')}
            required
            error={confirmPassword && newPassword !== confirmPassword ? t('auth.acceptInvite.passwordMismatch', 'Passwords do not match') : undefined}
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            tabIndex={-1}
          >
            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        {/* Consent checkbox */}
        <div className="accept-invite-consent">
          <label className="consent-label">
            <input
              type="checkbox"
              checked={consentAccepted}
              onChange={(e) => setConsentAccepted(e.target.checked)}
              className="consent-checkbox"
            />
            <span className="consent-text">
              {t('auth.acceptInvite.consentText', "J'accepte les")}{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="consent-link">
                {t('auth.acceptInvite.termsLink', "Conditions Générales d'Utilisation")}
              </a>
              {' '}{t('auth.acceptInvite.consentAnd', 'et la')}{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="consent-link">
                {t('auth.acceptInvite.privacyLink', 'Politique de Confidentialité')}
              </a>
              {' '}{t('auth.acceptInvite.consentOf', 'de CoreX-Suite.')}
            </span>
          </label>
        </div>

        {/* Submit button */}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={acceptInviteMutation.isPending}
          loadingText={t('auth.acceptInvite.accepting', 'Setting up account...')}
          disabled={!newPassword || !confirmPassword || newPassword !== confirmPassword || !consentAccepted}
          glow
        >
          {t('auth.acceptInvite.submit', 'Accept & Set Password')}
        </Button>

        {/* Back to login link */}
        <div className="accept-invite-footer">
          <span className="footer-text">{t('auth.acceptInvite.alreadyHaveAccount', 'Already have an account?')}</span>
          <button
            type="button"
            className="footer-link"
            onClick={() => navigate('/auth/login')}
          >
            {t('auth.acceptInvite.signIn', 'Sign in')}
          </button>
        </div>
      </form>

      <style>{`
        .accept-invite-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .accept-invite-error-msg {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: var(--color-error-light);
          border: 1px solid var(--color-error);
          border-radius: var(--radius-lg);
          color: var(--color-error);
          font-size: var(--text-sm);
          animation: shake 0.3s ease-in-out;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          75% { transform: translateX(4px); }
        }

        .accept-invite-info {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: var(--color-success-light);
          border: 1px solid var(--color-success);
          border-radius: var(--radius-lg);
          color: var(--color-success);
          font-size: var(--text-sm);
        }

        .password-field {
          position: relative;
        }

        .password-toggle {
          position: absolute;
          right: 12px;
          top: 38px;
          background: transparent;
          border: none;
          padding: 4px;
          color: var(--color-text-muted);
          cursor: pointer;
          transition: color 0.2s ease;
        }

        .password-toggle:hover {
          color: var(--color-text-primary);
        }

        .accept-invite-consent {
          padding: 12px 14px;
          background: var(--color-info-light, color-mix(in srgb, var(--color-brand-primary) 6%, transparent));
          border: 1px solid var(--color-border-default);
          border-radius: var(--radius-lg);
        }

        .consent-label {
          display: flex;
          gap: 10px;
          align-items: flex-start;
          cursor: pointer;
        }

        .consent-checkbox {
          width: 16px;
          height: 16px;
          margin-top: 2px;
          flex-shrink: 0;
          accent-color: var(--color-brand-primary);
          cursor: pointer;
        }

        .consent-text {
          font-size: var(--text-sm);
          color: var(--color-text-secondary);
          line-height: 1.5;
        }

        .consent-link {
          color: var(--color-brand-primary);
          text-decoration: underline;
          text-underline-offset: 2px;
        }

        .consent-link:hover { opacity: 0.8; }

        .accept-invite-footer {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          padding-top: 8px;
        }

        .footer-text {
          color: var(--color-text-muted);
          font-size: var(--text-sm);
        }

        .footer-link {
          background: transparent;
          border: none;
          color: var(--color-brand-primary);
          font-size: var(--text-sm);
          font-weight: 500;
          cursor: pointer;
          transition: opacity 0.2s ease;
        }

        .footer-link:hover {
          opacity: 0.8;
        }

        @media (max-width: 640px) {
          .accept-invite-form {
            gap: 1rem;
          }
          .accept-invite-footer {
            flex-wrap: wrap;
            justify-content: center;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .accept-invite-error-msg { animation: none !important; }
        }
      `}</style>
    </AuthLayout>
  );
}

export default AcceptInvitePage;
